import { Trade } from './types';
import type { FlowMetricMode } from './presets';
import { calculateDirectionalFlowTotals } from './aggregateOptionFlow';
import { getTradeExecutionMs } from 'utils/shared/trades/getOptionFlowTimestampMs';

export interface ChainRow {
  key: string;
  ticker: string;
  date: string;
  cp: string;
  strike: number;
  expiry: string;
  chainLabel: string;
  avgSpot: number;
  avgOtmPercent: number;
  avgPrice: number;
  totalSize: number;
  openInterest: number;
  avgImpliedVol: number;
  totalPremium: number;
  netCount: number;
  netSize: number;
  netPremium: number;
  directionalPremiumTotal: number;
  directionalSizeTotal: number;
  avgHeatScore: number;
  count: number;
  trades: Trade[];
}

interface GroupOptions {
  /** When true, include ticker in the grouping key (for multi-ticker pages) */
  includeTicker?: boolean;
  metricMode?: FlowMetricMode;
}

/**
 * Premium-weighted chain grouping.
 *
 * Groups trades by (cp | strike | expiry), optionally including ticker.
 *
 * weight_i = abs(premium_i)
 * W = sum(weight_i)
 * Weighted avg = sum(value_i * weight_i) / W
 *
 * Fallback: if W === 0 (all premiums missing), use simple averages.
 *
 * Uses a single-pass aggregation per group instead of multiple iterations.
 */
export default function groupTradesIntoChain(trades: Trade[], options?: GroupOptions): ChainRow[] {
  const includeTicker = options?.includeTicker ?? false;
  const metricMode = options?.metricMode || 'total';
  const groupMap = new Map<string, Trade[]>();

  for (const trade of trades) {
    const expiry = `${trade.expireYear}-${String(trade.expireMonth).padStart(2, '0')}-${String(
      trade.expireDay,
    ).padStart(2, '0')}`;
    const key = includeTicker
      ? `${trade.ticker}|${trade.cp}|${trade.strike}|${expiry}`
      : `${trade.cp}|${trade.strike}|${expiry}`;

    let group = groupMap.get(key);
    if (!group) {
      group = [];
      groupMap.set(key, group);
    }
    group.push(trade);
  }

  const rows: ChainRow[] = [];

  groupMap.forEach((group, key) => {
    const first = group[0];
    const count = group.length;
    const expiry = `${first.expireYear}-${String(first.expireMonth).padStart(2, '0')}-${String(
      first.expireDay,
    ).padStart(2, '0')}`;

    // Single-pass: accumulate all aggregation values at once
    let totalWeight = 0;
    let totalSize = 0;
    let totalPremium = 0;
    // Per-field accumulators: weightedSum, validWeight, simpleSum, validCount
    let spotWS = 0,
      spotVW = 0,
      spotSS = 0,
      spotVC = 0;
    let otmWS = 0,
      otmVW = 0,
      otmSS = 0,
      otmVC = 0;
    let priceWS = 0,
      priceVW = 0,
      priceSS = 0,
      priceVC = 0;
    let ivWS = 0,
      ivVW = 0,
      ivSS = 0,
      ivVC = 0;
    let heatWS = 0,
      heatVW = 0,
      heatSS = 0,
      heatVC = 0;
    let latestTimestamp = 0;
    let openInterest = 0;
    let maxOI = 0;

    for (const t of group) {
      const w = Math.abs(t.premium || 0);
      totalWeight += w;
      totalSize += t.size || 0;
      totalPremium += t.premium || 0;

      if (t.spot != null) {
        spotWS += t.spot * w;
        spotVW += w;
        spotSS += t.spot;
        spotVC++;
      }
      if (t.otmPercent != null) {
        otmWS += t.otmPercent * w;
        otmVW += w;
        otmSS += t.otmPercent;
        otmVC++;
      }
      if (t.price != null) {
        priceWS += t.price * w;
        priceVW += w;
        priceSS += t.price;
        priceVC++;
      }
      if (t.impliedVol != null) {
        ivWS += t.impliedVol * w;
        ivVW += w;
        ivSS += t.impliedVol;
        ivVC++;
      }
      if (t.scoreWidth != null) {
        heatWS += t.scoreWidth * w;
        heatVW += w;
        heatSS += t.scoreWidth;
        heatVC++;
      }

      const oi = t.openInterest || 0;
      if (oi > maxOI) maxOI = oi;
      const tradeTimestamp = getTradeExecutionMs(t);
      if (tradeTimestamp !== undefined && tradeTimestamp > latestTimestamp) {
        latestTimestamp = tradeTimestamp;
        openInterest = oi;
      }
    }

    if (latestTimestamp === 0) openInterest = maxOI;

    // Resolve weighted or simple average per field
    const usePremiumWeight = totalWeight > 0;
    const wavg = (ws: number, vw: number, ss: number, vc: number) =>
      usePremiumWeight ? (vw > 0 ? ws / vw : 0) : vc > 0 ? ss / vc : 0;

    const avgSpot = wavg(spotWS, spotVW, spotSS, spotVC);
    const avgOtmPercent = wavg(otmWS, otmVW, otmSS, otmVC);
    const avgPrice = wavg(priceWS, priceVW, priceSS, priceVC);
    const avgImpliedVol = wavg(ivWS, ivVW, ivSS, ivVC);
    const avgHeatScore = Math.round(wavg(heatWS, heatVW, heatSS, heatVC));
    const directional = calculateDirectionalFlowTotals(group);

    const cpLabel = first.cp === 'Call' || first.cp === 'C' ? 'Call' : 'Put';
    const chainLabel = `$${first.strike} ${cpLabel} ${expiry}`;

    // Sort child trades by time desc, tie-break by abs(premium) desc
    const sortedTrades = [...group].sort((a, b) => {
      const timeDiff = (getTradeExecutionMs(b) || 0) - (getTradeExecutionMs(a) || 0);
      if (timeDiff !== 0) return timeDiff;
      return Math.abs(b.premium || 0) - Math.abs(a.premium || 0);
    });

    const mostRecentDate = sortedTrades[0]?.date || first.date;

    rows.push({
      key,
      ticker: first.ticker || '',
      date: mostRecentDate,
      cp: first.cp || '',
      strike: first.strike,
      expiry,
      chainLabel,
      avgSpot,
      avgOtmPercent,
      avgPrice,
      totalSize,
      openInterest,
      avgImpliedVol,
      totalPremium,
      netCount: directional.count.net,
      netSize: directional.contracts.net,
      netPremium: directional.premium.net,
      directionalPremiumTotal: directional.premium.directionalTotal,
      directionalSizeTotal: directional.contracts.directionalTotal,
      avgHeatScore,
      count,
      trades: sortedTrades,
    });
  });

  if (metricMode === 'net') {
    return rows.sort((a, b) => b.netPremium - a.netPremium);
  }

  return rows;
}
